import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import { CONSOLE_LOCALES, api, apiDownload, apiRaw, describeError, getLocale } from '../api';
import { CountedField, FileButton, JsonEditor, Json, Message, NAME_MAX, Pill, Section, fmtTime, useJsonText } from '../components/ui';
import { useSession } from '../session';
import { CharacterAiPanel, aiComplete, receiptText, type Card as AiCard } from './CharacterAi';

interface Tag { id: string; name: string; color: string | null; color2?: string | null; category?: string | null }
/** add-tag-taxonomy: `GET /api/tags/taxonomy` — one locale (Accept-Language), colours on the category. */
interface Taxonomy { version: number; locale: string; default_locale: string; categories: Array<{ key: string; color: string; color2: string; name: string; sort: number }>; tags: Array<{ key: string; category: string; name: string; sort: number }> }
interface ResolvedTags { platform: Array<{ key: string; matched_from: string; category: string; name: string }>; existing_user: Array<{ id: string; name: string }>; new_user: string[]; unknown_keys: string[] }
/** change 8: `data.extensions.pockettavern` — MVP presentation fields that ride along with the card. */
interface PtExt { tagline?: string; gender?: 'male' | 'female' | 'other' | 'unknown'; avatar_color?: string; avatar_text?: string; source?: string; extra_books?: string[]; tag_keys?: string[] }
interface Summary { id: string; name: string; avatar_url: string | null; fav: boolean; folder_id: string | null; tag_ids: string[]; platform_tag_keys: string[]; tags: string[]; updated_at: string; deleted_at?: string | null; creator?: string; pockettavern: PtExt; description_excerpt: string; chat_count: number; last_chat_at: string | null }
interface Character extends Summary { card: { spec: string; data: Record<string, unknown> }; assets: Array<{ id: string; type: string; name: string; ext: string; status: string; url: string | null }>; source_hash: string }
interface Preview {
  job_id: string; format: string; expires_at: string; avatar_url: string | null;
  card_summary: { name: string; creator: string; character_version: string; tags: string[]; description_chars: number; has_avatar: boolean; spec_version_in: string };
  has_embedded_book: boolean; embedded_book_entries: number;
  assets: Array<{ name: string; type: string; ext: string; embedded: boolean }>;
  conflicts: Array<{ type: string; character_id: string; name: string }>;
  resolved_tags: ResolvedTags;
  warnings: string[];
  /** change 13: normalized CCv3, editable before confirm */
  card: { spec: string; data: Record<string, unknown> };
}
interface Meta { page: number; per_page: number; total: number }

const NEUTRAL = { color: '#a1a1aa', color2: '#27272a' };
/** Category colour lookup: platform tags inherit their category; user tags use `category` when set, else their own colours, else neutral. */
function chipStyle(taxonomy: Taxonomy | null, categoryKey: string | null | undefined, own?: { color?: string | null; color2?: string | null }): CSSProperties {
  const cat = categoryKey ? taxonomy?.categories.find((c) => c.key === categoryKey) : undefined;
  const color = cat?.color ?? own?.color ?? NEUTRAL.color;
  const color2 = cat?.color2 ?? own?.color2 ?? NEUTRAL.color2;
  return { color, background: color2, borderColor: color };
}
function TagChip({ label, style, active, onClick, title }: { label: string; style?: CSSProperties; active?: boolean; onClick?: () => void; title?: string }) {
  const cls = `rounded-full border px-2 py-0.5 text-[11px] ${active ? 'ring-1 ring-white/70' : ''}`;
  if (onClick) return <button type="button" onClick={onClick} title={title} className={`${cls} cursor-pointer`} style={style}>{label}</button>;
  return <span title={title} className={cls} style={style}>{label}</span>;
}
const platformName = (taxonomy: Taxonomy | null, key: string) => taxonomy?.tags.find((t) => t.key === key)?.name ?? key;
const platformCategory = (taxonomy: Taxonomy | null, key: string) => taxonomy?.tags.find((t) => t.key === key)?.category;

export function LibraryPage() {
  const { env, me } = useSession();
  const [items, setItems] = useState<Summary[]>([]);
  const [meta, setMeta] = useState<Meta | null>(null);
  const [tags, setTags] = useState<Tag[]>([]);
  const [taxonomy, setTaxonomy] = useState<Taxonomy | null>(null);
  const [q, setQ] = useState('');
  const [fav, setFav] = useState(false);
  const [tagFilter, setTagFilter] = useState<string[]>([]);
  const [ptagFilter, setPtagFilter] = useState<string[]>([]);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [favFirst, setFavFirst] = useState(false); // add-mvp-protocol-gaps-3: ?sort=fav（收藏优先 = 产品「置顶」）
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Character | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [auto, setAuto] = useState(false);
  const [confirmOpts, setConfirmOpts] = useState({ name_override: '', embedded_book: 'keep', tag_mode: 'create' });
  const [previewText, setPreviewText] = useState('');
  const [previewDirty, setPreviewDirty] = useState(false);
  const [previewErr, setPreviewErr] = useState<string | null>(null);
  /** harden-import-confirm: the last confirmed job is a 24 h receipt — kept around to exercise replay / GET / DELETE. */
  const [receipt, setReceipt] = useState<{ job_id: string; character_id: string; name: string; log: string[] } | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!me) return;
    setErr(null);
    try {
      const params = new URLSearchParams({ page: String(page), per_page: '20', sort: favFirst ? 'fav' : 'updated_at', order: 'desc' });
      if (q) params.set('q', q);
      if (fav) params.set('fav', '1');
      if (includeDeleted) params.set('include_deleted', '1');
      for (const t of tagFilter) params.append('tag', t);
      for (const k of ptagFilter) params.append('ptag', k);
      const [list, tagRes, tax] = await Promise.all([api<Summary[]>(`/api/characters?${params}`), api<Tag[]>('/api/tags'), api<Taxonomy>('/api/tags/taxonomy').catch(() => null)]);
      setItems(list.data);
      setMeta((list as unknown as { meta?: Meta }).meta ?? null);
      setTags(tagRes.data);
      if (tax) setTaxonomy(tax.data);
    } catch (e) {
      setErr(describeError(e));
    }
  }, [me, page, q, fav, favFirst, includeDeleted, tagFilter, ptagFilter]);

  useEffect(() => {
    void load();
  }, [load, env]);

  const open = async (id: string) => {
    try {
      setSelected((await api<Character>(`/api/characters/${id}`)).data);
    } catch (e) {
      setErr(describeError(e));
    }
  };

  const run = async (fn: () => Promise<string | void>) => {
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      const m = await fn();
      if (m) setMsg(m);
      await load();
    } catch (e) {
      setErr(describeError(e));
    } finally {
      setBusy(false);
    }
  };

  const importFile = (file: File) =>
    run(async () => {
      const res = await apiRaw(`/api/characters/import${auto ? '?auto=1' : ''}&filename=${encodeURIComponent(file.name)}`.replace('&filename', auto ? '&filename' : '?filename'), { method: 'POST', body: file, headers: { 'Content-Type': file.type || 'application/octet-stream' } });
      const body = (await res.json()) as { success: boolean; data: Preview | { character: Character; world_book_id?: string }; error?: { code: string; message: string } };
      if (!body.success) throw new Error(`${res.status} ${body.error?.code} — ${body.error?.message}`);
      if (auto) {
        const r = body.data as { character: Character; world_book_id?: string };
        setSelected(r.character);
        return `已一步导入 ${r.character.name}${r.world_book_id ? `（世界书 ${r.world_book_id}）` : ''}`;
      }
      const p = body.data as Preview;
      setPreview(p);
      setPreviewText(JSON.stringify(p.card, null, 2));
      setPreviewDirty(false);
      setPreviewErr(null);
      setConfirmOpts({ name_override: '', embedded_book: 'keep', tag_mode: 'create' });
      return `预览就绪：${p.card_summary.name}（${p.format}），${p.conflicts.length} 个冲突，${p.warnings.length} 条警告`;
    });

  const confirm = () =>
    run(async () => {
      if (!preview) return;
      const body: Record<string, unknown> = { embedded_book: confirmOpts.embedded_book, tag_mode: confirmOpts.tag_mode };
      if (confirmOpts.name_override.trim()) body.name_override = confirmOpts.name_override.trim();
      // change 13: edited preview card rides along with the confirmation (server re-normalizes; assets stay server-owned)
      if (previewDirty) {
        try {
          body.card = JSON.parse(previewText) as unknown;
          setPreviewErr(null);
        } catch (e) {
          setPreviewErr(e instanceof Error ? e.message : String(e));
          throw new Error('预览卡 JSON 无法解析');
        }
      }
      const { data } = await api<{ character: Character; world_book_id?: string; replayed?: true }>(`/api/characters/import/${preview.job_id}`, { method: 'POST', body });
      setPreview(null);
      setSelected(data.character);
      setReceipt({ job_id: preview.job_id, character_id: data.character.id, name: data.character.name, log: [`POST → 201 character ${data.character.id}${data.replayed ? ' (replayed)' : ''}`] });
      return `已导入 ${data.character.name}${data.world_book_id ? `，抽出世界书 ${data.world_book_id}` : ''}`;
    });

  const cancelPreview = () =>
    run(async () => {
      if (!preview) return;
      await api(`/api/characters/import/${preview.job_id}`, { method: 'DELETE' });
      setPreview(null);
      return '已取消导入';
    });

  // ── harden-import-confirm verification: replay / receipt GET / DELETE on the confirmed job ──
  const receiptLog = (line: string) => setReceipt((r) => (r ? { ...r, log: [...r.log, line] } : r));
  const replayConfirm = () =>
    run(async () => {
      if (!receipt) return;
      const { data } = await api<{ character: Character; replayed?: true }>(`/api/characters/import/${receipt.job_id}`, { method: 'POST', body: { name_override: 'Replay Should Be Ignored' } });
      const same = data.character.id === receipt.character_id;
      receiptLog(`POST(again) → 201 character ${data.character.id} · replayed=${String(data.replayed ?? false)} · same id=${same ? '✓' : '✗'} · name=${data.character.name}`);
      return same && data.replayed ? '幂等回放正常：同一角色，replayed=true，body 被忽略' : '异常：回放返回了不同角色或缺少 replayed';
    });
  const inspectReceipt = () =>
    run(async () => {
      if (!receipt) return;
      const { data } = await api<{ status: string; expires_at: string; result?: { character: Character } }>(`/api/characters/import/${receipt.job_id}`);
      receiptLog(`GET → 200 status=${data.status} · result.character=${data.result?.character.id ?? '—'} · expires_at=${data.expires_at}`);
      return `job 状态 ${data.status}`;
    });
  const cancelReceipt = () =>
    run(async () => {
      if (!receipt) return;
      try {
        await api(`/api/characters/import/${receipt.job_id}`, { method: 'DELETE' });
        receiptLog('DELETE → 200 ✗（预期 404：收据不可取消）');
        return '异常：已确认 job 被取消了';
      } catch (e) {
        const text = describeError(e);
        receiptLog(`DELETE → ${text}`);
        return text.includes('LIBRARY_IMPORT_JOB_NOT_FOUND') ? '符合预期：已确认 job 不可取消（404），收据保留' : `意外错误：${text}`;
      }
    });

  const toggleTagFilter = (id: string) => setTagFilter((cur) => (cur.includes(id) ? cur.filter((t) => t !== id) : [...cur, id]));
  const togglePtagFilter = (key: string) => setPtagFilter((cur) => (cur.includes(key) ? cur.filter((t) => t !== key) : [...cur, key]));

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
      <div className="space-y-4">
        <Section
          title={`角色库 ${meta ? `· ${meta.total} 个` : ''}`}
          actions={
            <>
              <label className="flex items-center gap-1 text-xs text-zinc-400"><input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} /> 一步导入</label>
              <FileButton label="导入卡（PNG/JSON/YAML/charx/byaf）" accept=".png,.json,.yaml,.yml,.charx,.byaf,.zip" onFile={importFile} disabled={busy || !me} />
            </>
          }
        >
          {!me && <Message text="请先在「账号」页登录" />}
          <div className="flex flex-wrap items-center gap-2">
            <input className="input w-56" placeholder="搜索名称" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
            <label className="flex items-center gap-1 text-xs text-zinc-400"><input type="checkbox" checked={fav} onChange={(e) => setFav(e.target.checked)} /> 仅收藏</label>
            <label className="flex items-center gap-1 text-xs text-zinc-400" title="GET /api/characters?sort=fav：收藏优先再按 updated_at"><input type="checkbox" checked={favFirst} onChange={(e) => setFavFirst(e.target.checked)} /> 收藏优先</label>
            <label className="flex items-center gap-1 text-xs text-zinc-400"><input type="checkbox" checked={includeDeleted} onChange={(e) => setIncludeDeleted(e.target.checked)} /> 含已删除</label>
          </div>
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-1">
              <span className="text-[11px] text-zinc-500">用户标签</span>
              {tags.map((t) => (
                <TagChip key={t.id} label={t.name} active={tagFilter.includes(t.id)} style={chipStyle(taxonomy, t.category, t)} onClick={() => toggleTagFilter(t.id)} title={t.category ? `分类 ${t.category}` : '未分类'} />
              ))}
              <NewTag taxonomy={taxonomy} onCreated={load} />
            </div>
            {taxonomy && (
              <div className="flex flex-wrap items-center gap-1">
                <span className="text-[11px] text-zinc-500" title={`GET /api/tags/taxonomy · locale ${taxonomy.locale} · v${taxonomy.version}`}>平台标签（{taxonomy.locale}）</span>
                {taxonomy.categories.map((c) => (
                  <span key={c.key} className="flex flex-wrap items-center gap-1 rounded-lg border border-zinc-800 px-1.5 py-0.5">
                    <span className="text-[10px]" style={{ color: c.color }}>{c.name}</span>
                    {taxonomy.tags.filter((t) => t.category === c.key).map((t) => (
                      <TagChip key={t.key} label={t.name} active={ptagFilter.includes(t.key)} style={chipStyle(taxonomy, c.key)} onClick={() => togglePtagFilter(t.key)} title={t.key} />
                    ))}
                  </span>
                ))}
              </div>
            )}
          </div>
          <Message text={err} kind="error" />
          <Message text={msg} kind="ok" />
          <ul className="divide-y divide-zinc-800">
            {items.map((c) => (
              <li key={c.id} className={`flex cursor-pointer items-center gap-3 py-2 ${selected?.id === c.id ? 'bg-zinc-800/40' : ''}`} onClick={() => void open(c.id)}>
                {c.avatar_url ? (
                  <img src={`${c.avatar_url}?size=64`} alt="" className="h-10 w-10 rounded-lg object-cover" />
                ) : (
                  // colour-block avatar (MVP): extensions.pockettavern.avatar_color / avatar_text, falling back to the first character of the name
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg text-sm font-semibold text-zinc-100" style={{ background: c.pockettavern?.avatar_color ?? '#3f3f46' }} title="无头像图片：色块头像">{c.pockettavern?.avatar_text ?? Array.from(c.name)[0] ?? '?'}</div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="truncate">{c.name}</span>
                    {c.fav && <Pill tone="amber">★</Pill>}
                    {c.pockettavern?.source && <Pill tone="indigo">{c.pockettavern.source}</Pill>}
                    {c.deleted_at && <Pill tone="rose">已删除</Pill>}
                  </div>
                  {(c.pockettavern?.tagline || c.description_excerpt) && <div className="truncate text-[11px] text-zinc-400">{c.pockettavern?.tagline ?? c.description_excerpt}</div>}
                  <div className="flex flex-wrap items-center gap-1 text-[11px] text-zinc-500">
                    {c.platform_tag_keys.map((k) => <TagChip key={k} label={platformName(taxonomy, k)} style={chipStyle(taxonomy, platformCategory(taxonomy, k))} title={`平台 ${k}`} />)}
                    {c.tag_ids.map((id) => { const t = tags.find((x) => x.id === id); return <TagChip key={id} label={`#${t?.name ?? id}`} style={chipStyle(taxonomy, t?.category, t)} title="用户标签" />; })}
                    {(c.tags ?? []).filter((raw) => {
                      const n = raw.trim().toLowerCase();
                      if (!n) return false;
                      if (c.platform_tag_keys.some((k) => k.toLowerCase() === n || platformName(taxonomy, k).toLowerCase() === n)) return false;
                      if (c.tag_ids.some((id) => (tags.find((x) => x.id === id)?.name ?? '').toLowerCase() === n)) return false;
                      return true;
                    }).map((raw) => <TagChip key={`card:${raw}`} label={raw} title="卡内 tags" />)}
                    <span>· {c.chat_count} 个会话{c.last_chat_at ? ` · 最近 ${fmtTime(c.last_chat_at)}` : ''}</span>
                    <span>· {fmtTime(c.updated_at)}</span>
                  </div>
                </div>
              </li>
            ))}
            {items.length === 0 && <li className="py-6 text-center text-xs text-zinc-500">空。导入一张 SillyTavern PNG 卡试试。</li>}
          </ul>
          {meta && meta.total > meta.per_page && (
            <div className="flex items-center gap-2 text-xs text-zinc-400">
              <button className="btn-ghost" disabled={page <= 1} onClick={() => setPage(page - 1)}>上一页</button>
              <span>{page} / {Math.ceil(meta.total / meta.per_page)}</span>
              <button className="btn-ghost" disabled={page * meta.per_page >= meta.total} onClick={() => setPage(page + 1)}>下一页</button>
            </div>
          )}
        </Section>
        {receipt && !preview && (
          <Section
            title={`导入收据 · ${receipt.name}`}
            actions={
              <>
                <button className="btn" disabled={busy} onClick={() => void replayConfirm()} title="再次 POST 同一 job（带不同 body）→ 预期 201 同一角色 + replayed=true">重复确认（验证幂等）</button>
                <button className="btn-ghost" disabled={busy} onClick={() => void inspectReceipt()} title="GET job → 预期 200 status=confirmed + result">查看 job</button>
                <button className="btn-ghost" disabled={busy} onClick={() => void cancelReceipt()} title="DELETE job → 预期 404（收据保留）">取消（预期 404）</button>
                <button className="btn-ghost" disabled={busy} onClick={() => setReceipt(null)}>关闭</button>
              </>
            }
          >
            <div className="text-xs text-zinc-400">job <code>{receipt.job_id}</code> → character <code>{receipt.character_id}</code>（harden-import-confirm：确认后 24 h 内同一 job 可重复确认 / 读取，不新建角色）</div>
            <ul className="mt-2 space-y-0.5 font-mono text-[11px] text-zinc-300">{receipt.log.map((l, i) => <li key={i}>{l}</li>)}</ul>
          </Section>
        )}
        {preview && (
          <Section title={`导入预览 · ${preview.card_summary.name}`} actions={<><button className="btn" disabled={busy} onClick={() => void confirm()}>确认导入</button><button className="btn-ghost" disabled={busy} onClick={() => void cancelPreview()}>取消</button></>}>
            <div className="flex items-start gap-3">
              {preview.avatar_url ? (
                <img src={`${preview.avatar_url}?size=64`} alt="" className="h-16 w-16 shrink-0 rounded-lg object-cover" />
              ) : (
                <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg bg-zinc-800 text-[11px] text-zinc-500">无头像</div>
              )}
              <div className="grid min-w-0 flex-1 gap-2 text-xs text-zinc-300 sm:grid-cols-2">
              <div>格式 <Pill tone="indigo">{preview.format}</Pill> 源 spec <Pill>{preview.card_summary.spec_version_in}</Pill> 头像 {preview.card_summary.has_avatar ? '有' : '无'}</div>
              <div>作者 {preview.card_summary.creator || '—'} · 版本 {preview.card_summary.character_version || '—'} · 文本 {preview.card_summary.description_chars} 字</div>
              <div>标签 {preview.card_summary.tags.join(', ') || '—'}</div>
              <div>内嵌世界书 {preview.has_embedded_book ? `${preview.embedded_book_entries} 条` : '无'} · 资产 {preview.assets.length}（内嵌 {preview.assets.filter((a) => a.embedded).length}）</div>
              </div>
            </div>
            {preview.conflicts.length > 0 && <Message text={`冲突：${preview.conflicts.map((c) => `${c.type} → ${c.name} (${c.character_id.slice(0, 8)})`).join('; ')}`} />}
            {preview.warnings.length > 0 && <Message text={`警告：${preview.warnings.join('; ')}`} />}
            <div className="space-y-1 rounded border border-zinc-800 p-2 text-[11px]">
              <div className="text-zinc-400">标签解析 <span className="font-mono text-zinc-500">resolved_tags</span>（① tag_keys → ② 全语言译名 / 别名 → ③ 已有用户标签 → ④ 新建）</div>
              <div className="flex flex-wrap items-center gap-1"><span className="text-emerald-300">✓ 平台</span>{preview.resolved_tags.platform.map((p) => <TagChip key={p.key} label={`${p.name}`} style={chipStyle(taxonomy, p.category)} title={`${p.key} ← ${p.matched_from}`} />)}{preview.resolved_tags.platform.length === 0 && <span className="text-zinc-600">（无）</span>}</div>
              <div className="flex flex-wrap items-center gap-1"><span className="text-indigo-300">✓ 已有用户标签</span>{preview.resolved_tags.existing_user.map((t) => <TagChip key={t.id} label={`#${t.name}`} style={chipStyle(taxonomy, tags.find((x) => x.id === t.id)?.category, tags.find((x) => x.id === t.id))} />)}{preview.resolved_tags.existing_user.length === 0 && <span className="text-zinc-600">（无）</span>}</div>
              <div className="flex flex-wrap items-center gap-1"><span className="text-amber-300">+ 将新建（tag_mode=create）</span>{preview.resolved_tags.new_user.map((n) => <Pill key={n} tone="amber">{n}</Pill>)}{preview.resolved_tags.new_user.length === 0 && <span className="text-zinc-600">（无）</span>}</div>
              {preview.resolved_tags.unknown_keys.length > 0 && <div className="flex flex-wrap items-center gap-1"><span className="text-rose-300">⚠ 未知平台 key（不落库）</span>{preview.resolved_tags.unknown_keys.map((k) => <Pill key={k} tone="rose">{k}</Pill>)}</div>}
            </div>
            <div className="grid gap-2 sm:grid-cols-3">
              <div><span className="label">改名（可选，精修 ≤ 40）</span><CountedField limit={NAME_MAX} value={confirmOpts.name_override} onChange={(v) => setConfirmOpts({ ...confirmOpts, name_override: v })} /></div>
              <div><span className="label">内嵌世界书</span>
                <select className="input" value={confirmOpts.embedded_book} onChange={(e) => setConfirmOpts({ ...confirmOpts, embedded_book: e.target.value })}>
                  <option value="keep">keep（保留在卡内）</option><option value="extract">extract（抽为独立世界书）</option><option value="both">both</option><option value="drop">drop</option>
                </select></div>
              <div><span className="label">标签</span>
                <select className="input" value={confirmOpts.tag_mode} onChange={(e) => setConfirmOpts({ ...confirmOpts, tag_mode: e.target.value })}><option value="create">create（自动建标签）</option><option value="ignore">ignore</option></select></div>
            </div>
            <div>
              <div className="flex items-center justify-between"><span className="label">归一化卡（CCv3，可编辑后随确认提交 · data.assets 由服务端管）{previewDirty && <Pill tone="amber">已编辑</Pill>}</span><button className="btn-ghost py-0 text-[11px]" onClick={() => { setPreviewText(JSON.stringify(preview.card, null, 2)); setPreviewDirty(false); }}>还原</button></div>
              <JsonEditor value={previewText} onChange={(t) => { setPreviewText(t); setPreviewDirty(true); }} error={previewErr} rows={12} />
            </div>
            <div className="text-[11px] text-zinc-500">job {preview.job_id} · 过期 {fmtTime(preview.expires_at)}</div>
          </Section>
        )}
        <CharacterAiPanel onSaved={() => void load()} />
      </div>
      <div>
        {selected ? <CharacterDetail key={selected.id + selected.updated_at} character={selected} tags={tags} taxonomy={taxonomy} onChanged={(c) => { setSelected(c); void load(); }} onClosed={() => { setSelected(null); void load(); }} /> : <Section title="详情"><div className="text-xs text-zinc-500">从左侧选择一个角色。</div></Section>}
      </div>
    </div>
  );
}

function NewTag({ taxonomy, onCreated }: { taxonomy: Taxonomy | null; onCreated: () => Promise<void> | void }) {
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const create = async () => {
    if (!name.trim()) return;
    try {
      await api('/api/tags', { method: 'POST', body: { name: name.trim(), ...(category ? { category } : {}) } });
      setName('');
      setErr(null);
      await onCreated();
    } catch (e) {
      setErr(describeError(e));
    }
  };
  return (
    <span className="flex items-center gap-1">
      <input className="input w-28 py-0.5 text-[11px]" placeholder="+ 标签" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && void create()} title={err ?? ''} />
      <select className="input w-auto py-0.5 text-[11px]" value={category} onChange={(e) => setCategory(e.target.value)} title="可选：按平台分类着色（tags.category）">
        <option value="">未分类</option>
        {taxonomy?.categories.map((c) => <option key={c.key} value={c.key}>{c.name}</option>)}
      </select>
      {err && <span className="text-[11px] text-rose-300">{err}</span>}
    </span>
  );
}

function CharacterDetail({ character, tags, taxonomy, onChanged, onClosed }: { character: Character; tags: Tag[]; taxonomy: Taxonomy | null; onChanged: (c: Character) => void; onClosed: () => void }) {
  const editor = useJsonText(character.card);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [tagIds, setTagIds] = useState<string[]>(character.tag_ids);
  const [platformKeys, setPlatformKeys] = useState<string[]>(character.platform_tag_keys);
  const [exportLocale, setExportLocale] = useState<string>(getLocale());

  const run = async (fn: () => Promise<Character | string | void>) => {
    setErr(null);
    setMsg(null);
    try {
      const r = await fn();
      if (typeof r === 'string') setMsg(r);
      else if (r) onChanged(r);
    } catch (e) {
      setErr(describeError(e));
    }
  };
  const patch = (body: Record<string, unknown>) => run(async () => (await api<Character>(`/api/characters/${character.id}`, { method: 'PATCH', body, headers: { 'If-Match': character.updated_at } })).data);
  const saveCard = () => {
    const card = editor.parse();
    if (card === undefined) return;
    void patch({ card });
  };
  // change 13: fill the empty fields of the card currently in the editor (does not save; press 保存 card afterwards)
  const completeWithAi = () =>
    run(async () => {
      const card = editor.parse();
      if (card === undefined) return;
      const r = await aiComplete(card);
      editor.reset(r.card as AiCard);
      return r.filled_fields.length ? `AI 补全了 ${r.filled_fields.join(', ')} · ${receiptText(r.receipt)}（未保存，请点「保存 card」）` : '没有空字段需要补全';
    });
  const uploadAvatar = (file: File) =>
    run(async () => {
      const res = await apiRaw(`/api/characters/${character.id}/avatar`, { method: 'PUT', body: file, headers: { 'Content-Type': file.type || 'image/png' } });
      const body = (await res.json()) as { success: boolean; data: Character; error?: { code: string; message: string } };
      if (!body.success) throw new Error(`${res.status} ${body.error?.code} — ${body.error?.message}`);
      return body.data;
    });
  const saveTags = () => run(async () => (await api<Character>(`/api/characters/${character.id}/tags`, { method: 'PUT', body: { tag_ids: tagIds } })).data);
  const savePlatformTags = () => run(async () => (await api<Character>(`/api/characters/${character.id}/platform-tags`, { method: 'PUT', body: { keys: platformKeys } })).data);
  // export: `?locale=` overrides the Accept-Language the console already sends; data.tags = names, extensions.pockettavern.tag_keys = keys
  const download = (format: string, spec?: string) => run(() => apiDownload(`/api/characters/${character.id}/export?format=${format}${spec ? `&spec=${spec}` : ''}&locale=${encodeURIComponent(exportLocale)}`, `${character.name}.${format}`).then(() => `已下载 ${format}（locale ${exportLocale}）`));
  // change 8: MVP fields live in data.extensions.pockettavern (one-level deep merge on PATCH keeps the other extension keys)
  const ext = ((character.card.data.extensions as { pockettavern?: PtExt } | undefined)?.pockettavern ?? {}) as PtExt;
  const [ptExt, setPtExt] = useState<PtExt>({ tagline: ext.tagline ?? '', gender: ext.gender, avatar_color: ext.avatar_color ?? '', avatar_text: ext.avatar_text ?? '', source: ext.source, extra_books: ext.extra_books ?? [] });
  const savePtExt = () => {
    const clean: Record<string, unknown> = { ...ext };
    for (const [k, v] of Object.entries(ptExt)) {
      if (v === undefined || v === '' || (Array.isArray(v) && v.length === 0)) delete clean[k];
      else clean[k] = v;
    }
    void patch({ card: { data: { extensions: { pockettavern: clean } } } });
  };

  return (
    <Section
      title={<span>{character.name} <span className="ml-2 font-mono text-[11px] text-zinc-500">{character.id}</span></span>}
      actions={<button className="btn-ghost" onClick={onClosed}>关闭</button>}
    >
      <div className="flex items-start gap-4">
        {character.avatar_url ? <img src={character.avatar_url} alt="" className="h-28 w-28 rounded-xl object-cover" /> : <div className="flex h-28 w-28 items-center justify-center rounded-xl bg-zinc-800 text-xs text-zinc-500">无头像</div>}
        <div className="space-y-2 text-xs text-zinc-400">
          <div>更新 {fmtTime(character.updated_at)} · hash <span className="font-mono">{character.source_hash.slice(0, 12)}</span> · 资产 {character.assets.length}</div>
          <div className="flex flex-wrap gap-2">
            <FileButton label="换头像" accept="image/png,image/jpeg,image/webp" onFile={uploadAvatar} />
            <button className="btn-ghost" onClick={() => void patch({ fav: !character.fav })}>{character.fav ? '取消收藏' : '收藏'}</button>
            <button className="btn-ghost" onClick={() => void run(async () => (await api<Character>(`/api/characters/${character.id}/duplicate`, { method: 'POST' })).data)}>复制</button>
            {character.deleted_at ? (<>
              <button className="btn-ghost" onClick={() => void run(async () => (await api<Character>(`/api/characters/${character.id}/restore`, { method: 'POST' })).data)}>恢复</button>
            <button className="btn-ghost" title="POST /api/characters/{id}/restore?with_chats=1：连带恢复随它删除、仍在 30 天内的会话" onClick={() => void run(async () => (await api<Character>(`/api/characters/${character.id}/restore?with_chats=1`, { method: 'POST' })).data)}>恢复（连带会话）</button>
            </>) : (<>
              <button className="btn-ghost text-rose-300" onClick={() => void run(async () => { await api(`/api/characters/${character.id}`, { method: 'DELETE' }); onClosed(); })}>删除</button>
            <button className="btn-ghost text-rose-300" title="DELETE /api/characters/{id}?with_chats=1：同事务连带软删该角色全部单聊（群聊不动）" onClick={() => { if (!window.confirm(`删除「${character.name}」并连带删除它的全部单聊？（30 天内可用「恢复（连带会话）」找回）`)) return; void run(async () => { const { data } = await api<{ deleted: true; chats_deleted: number }>(`/api/characters/${character.id}?with_chats=1`, { method: 'DELETE' }); window.alert(`已删除角色，连带 ${data.chats_deleted} 个会话`); onClosed(); }); }}>删除（连带会话）</button>
            </>)}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button className="btn-ghost" onClick={() => void download('json')}>导出 JSON V3</button>
            <button className="btn-ghost" onClick={() => void download('json', 'v2')}>JSON V2</button>
            <button className="btn-ghost" onClick={() => void download('png')}>PNG</button>
            <button className="btn-ghost" onClick={() => void download('charx')}>charx</button>
            <select className="input w-auto py-0.5 text-[11px]" value={exportLocale} onChange={(e) => setExportLocale(e.target.value)} title="导出 data.tags 译名语言（?locale=）；tag_keys 不受影响">
              {CONSOLE_LOCALES.map((l) => <option key={l} value={l}>tags 译名 {l}</option>)}
              <option value="ja">tags 译名 ja（回退演示）</option>
            </select>
          </div>
        </div>
      </div>
      <div>
        <span className="label">用户标签（tag_ids）</span>
        <div className="flex flex-wrap items-center gap-1">
          {tags.map((t) => (
            <TagChip key={t.id} label={t.name} active={tagIds.includes(t.id)} style={{ ...chipStyle(taxonomy, t.category, t), opacity: tagIds.includes(t.id) ? 1 : 0.45 }} onClick={() => setTagIds((cur) => (cur.includes(t.id) ? cur.filter((x) => x !== t.id) : [...cur, t.id]))} />
          ))}
          <button className="btn-ghost py-0.5 text-[11px]" onClick={() => void saveTags()}>保存标签</button>
        </div>
      </div>
      <div>
        <span className="label">平台标签（platform_tag_keys · PUT …/platform-tags · 只存 key）</span>
        {taxonomy ? (
          <div className="space-y-1">
            {taxonomy.categories.map((c) => (
              <div key={c.key} className="flex flex-wrap items-center gap-1">
                <span className="w-16 text-[11px]" style={{ color: c.color }}>{c.name}</span>
                {taxonomy.tags.filter((t) => t.category === c.key).map((t) => (
                  <TagChip key={t.key} label={t.name} active={platformKeys.includes(t.key)} style={{ ...chipStyle(taxonomy, c.key), opacity: platformKeys.includes(t.key) ? 1 : 0.45 }} onClick={() => setPlatformKeys((cur) => (cur.includes(t.key) ? cur.filter((x) => x !== t.key) : [...cur, t.key]))} title={t.key} />
                ))}
              </div>
            ))}
            <div className="flex items-center gap-2 text-[11px] text-zinc-500">
              <button className="btn-ghost py-0.5 text-[11px]" onClick={() => void savePlatformTags()}>保存平台标签</button>
              <span className="font-mono">{character.platform_tag_keys.join(', ') || '（无）'}</span>
            </div>
          </div>
        ) : <div className="text-xs text-zinc-500">词表未加载。</div>}
      </div>
      <div className="space-y-1 rounded border border-zinc-800 p-2">
        <div className="flex items-center justify-between"><span className="label">MVP 字段（extensions.pockettavern）</span><button className="btn-ghost" onClick={savePtExt}>保存字段</button></div>
        <div className="grid gap-2 text-xs sm:grid-cols-3">
          <label className="space-y-0.5"><span className="text-zinc-400">tagline</span><CountedField value={ptExt.tagline ?? ''} onChange={(v) => setPtExt({ ...ptExt, tagline: v })} /></label>
          <label className="space-y-0.5"><span className="text-zinc-400">source</span><select className="input py-1" value={ptExt.source ?? ''} onChange={(e) => setPtExt({ ...ptExt, source: e.target.value || undefined })}><option value="">（未设）</option>{['st_import', 'official_template', 'ai_generated', 'chub', 'manual'].map((s) => <option key={s} value={s}>{s}</option>)}</select></label>
          <label className="space-y-0.5"><span className="text-zinc-400">avatar_color（#rrggbb）</span><div className="flex gap-1"><input className="input py-1" placeholder="#c9a66b" value={ptExt.avatar_color ?? ''} onChange={(e) => setPtExt({ ...ptExt, avatar_color: e.target.value })} /><span className="h-7 w-7 rounded" style={{ background: /^#[0-9a-fA-F]{6}$/.test(ptExt.avatar_color ?? '') ? ptExt.avatar_color : '#3f3f46' }} /></div></label>
          <label className="space-y-0.5"><span className="text-zinc-400">avatar_text（≤ 2 字）</span><input className="input py-1" maxLength={2} value={ptExt.avatar_text ?? ''} onChange={(e) => setPtExt({ ...ptExt, avatar_text: e.target.value })} /></label>
          <label className="space-y-0.5"><span className="text-zinc-400">extra_books（逗号分隔书名）</span><input className="input py-1" value={(ptExt.extra_books ?? []).join(', ')} onChange={(e) => setPtExt({ ...ptExt, extra_books: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })} /></label>
          <div className="space-y-0.5 text-zinc-500"><span className="text-zinc-400">tag_keys（只读）</span><div className="font-mono text-[11px]">{(ext.tag_keys ?? []).join(', ') || '（导出时由 platform_tag_keys 写入，导入时优先识别）'}</div></div>
          <div className="space-y-0.5 text-zinc-500"><span className="text-zinc-400">tags（卡内 data.tags）</span><div className="font-mono text-[11px]">{(character.tags ?? []).join(', ') || '（无）'}</div></div>
        </div>
        <div className="text-[11px] text-zinc-500">列表摘要：{character.chat_count} 个会话{character.last_chat_at ? ` · 最近 ${fmtTime(character.last_chat_at)}` : ''} · 简介 {character.description_excerpt ? `“${character.description_excerpt.slice(0, 60)}${character.description_excerpt.length > 60 ? '…' : ''}”` : '（空）'}</div>
      </div>
      <div>
        <div className="flex items-center justify-between"><span className="label">card（CCv3 JSON，If-Match: {character.updated_at}）</span><div className="flex gap-1"><button className="btn-ghost" onClick={() => void completeWithAi()} title="POST /api/characters/ai/complete：只填空字段（Pro）">✨ AI 补全空字段</button><button className="btn" onClick={saveCard}>保存 card</button></div></div>
        <JsonEditor value={editor.text} onChange={editor.setText} error={editor.error} rows={22} />
      </div>
      {character.assets.length > 0 && <Json value={character.assets} className="max-h-40" />}
      <Message text={err} kind="error" />
      <Message text={msg} kind="ok" />
    </Section>
  );
}
